import { DripHistoryItem } from "../../hooks/useDripHistory";

// Fallback decimals when a mint hasn't resolved on-chain yet (kySOL is 9).
const FALLBACK_DECIMALS = 9;

const toDayKey = (timestamp: string): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDayLabel = (dayKey: string): string => {
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const addDays = (dayKey: string, delta: number): string => {
  const [year, month, day] = dayKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + delta);
  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, "0");
  const nextDay = String(date.getDate()).padStart(2, "0");
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

export interface DripDailyRewardsSeries {
  mint: string;
  label: string;
  color: string;
  data: number[];
}

export interface DripDailyRewardsChartData {
  dayKeys: string[];
  labels: string[];
  series: DripDailyRewardsSeries[];
}

const CHART_COLORS = [
  "#5FF4F4",
  "#7B8CFF",
  "#FFB86B",
  "#FF79C6",
  "#50FA7B",
  "#BD93F9",
  "#F1FA8C",
  "#8BE9FD",
];

export const aggregateDripHistoryByDay = (
  history: DripHistoryItem[],
  resolveLabel: (mint: string) => string,
  decimalsByMint: Record<string, number>
): DripDailyRewardsChartData | null => {
  if (history.length === 0) return null;

  const totalsByDayAndMint = new Map<string, Map<string, number>>();

  for (const item of history) {
    const dayKey = toDayKey(item.timestamp);
    const decimals = decimalsByMint[item.mint] ?? FALLBACK_DECIMALS;
    const amount = Number(item.amount) / Math.pow(10, decimals);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    const dayTotals = totalsByDayAndMint.get(dayKey) ?? new Map<string, number>();
    dayTotals.set(item.mint, (dayTotals.get(item.mint) ?? 0) + amount);
    totalsByDayAndMint.set(dayKey, dayTotals);
  }

  if (totalsByDayAndMint.size === 0) return null;

  const sortedDayKeys = [...totalsByDayAndMint.keys()].sort();
  const firstDay = sortedDayKeys[0];
  const lastDay = sortedDayKeys[sortedDayKeys.length - 1];

  const dayKeys: string[] = [];
  for (let dayKey = firstDay; dayKey <= lastDay; dayKey = addDays(dayKey, 1)) {
    dayKeys.push(dayKey);
  }

  const mintSet = new Set<string>();
  for (const dayTotals of totalsByDayAndMint.values()) {
    for (const mint of dayTotals.keys()) {
      mintSet.add(mint);
    }
  }

  const mints = [...mintSet].sort((a, b) => {
    const totalA = history
      .filter((item) => item.mint === a)
      .reduce((sum, item) => sum + Number(item.amount), 0);
    const totalB = history
      .filter((item) => item.mint === b)
      .reduce((sum, item) => sum + Number(item.amount), 0);
    return totalB - totalA;
  });

  const series: DripDailyRewardsSeries[] = mints.map((mint, index) => ({
    mint,
    label: resolveLabel(mint),
    color: CHART_COLORS[index % CHART_COLORS.length],
    data: dayKeys.map((dayKey) => totalsByDayAndMint.get(dayKey)?.get(mint) ?? 0),
  }));

  return {
    dayKeys,
    labels: dayKeys.map(formatDayLabel),
    series,
  };
};
